import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, FileText, CheckCircle, AlertCircle, Users, Zap, Gavel, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import {
  Container,
  Section,
  SectionHeader
} from './modern/Layout';
import { ModernButton, ButtonGroup } from './modern/ModernButton';
import { FadeIn } from './animations/MotionComponents';

const Terms = () => {
  const [expandedSection, setExpandedSection] = useState(0);

  const termsData = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Service Acceptance',
      description: 'Terms and conditions of service',
      color: 'from-blue-500 to-blue-600',
      content: [
        'By accessing our platform, you accept these terms and conditions',
        'We reserve the right to modify terms at any time with notice',
        'Continued use constitutes acceptance of modified terms',
        'If you do not agree with any terms, please discontinue service',
        'These terms apply to all users and any sub-accounts'
      ]
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'User Responsibilities',
      description: 'Your obligations and commitments',
      color: 'from-green-500 to-green-600',
      content: [
        'Maintain confidentiality of your login credentials',
        'Do not use the service for illegal or unauthorized purposes',
        'Do not attempt to gain unauthorized access to systems',
        'Report security vulnerabilities immediately',
        'Comply with all applicable laws and regulations'
      ]
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Service Availability',
      description: 'Uptime commitments and maintenance',
      color: 'from-amber-500 to-amber-600',
      content: [
        '99.9% uptime SLA commitment',
        'Scheduled maintenance windows (with 48-hour notice)',
        'Emergency maintenance for security or critical issues',
        'Best-effort support during service disruptions',
        'Credits for significant SLA violations'
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Liability Limits',
      description: 'Limitation of liability',
      color: 'from-pink-500 to-pink-600',
      content: [
        'We are not liable for indirect or consequential damages',
        'Maximum liability limited to amount paid in last 12 months',
        'Not responsible for user errors or misconfigurations',
        'Not liable for third-party services or integrations',
        'Users assume all risk of data loss if backups not maintained'
      ]
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Intellectual Property',
      description: 'Ownership and usage rights',
      color: 'from-orange-500 to-orange-600',
      content: [
        'We retain all rights to the platform and intellectual property',
        'You retain ownership of your data and user-generated content',
        'License granted for personal and business use only',
        'No sublicensing, resale, or modification of platform allowed',
        'Attribution required for public use of our content'
      ]
    },
    {
      icon: <Gavel className="w-6 h-6" />,
      title: 'Legal Compliance',
      description: 'Jurisdiction and dispute resolution',
      color: 'from-purple-500 to-purple-600',
      content: [
        'Governed by laws of the jurisdiction of service',
        'You consent to exclusive jurisdiction of applicable courts',
        'Any disputes subject to binding arbitration',
        'We comply with GDPR, CCPA, and other data regulations',
        'Violations may result in immediate account termination'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-purple-50 to-white">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 left-10 w-72 h-72 bg-pink-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse delay-2000"></div>
        <div className="absolute -bottom-8 right-20 w-72 h-72 bg-blue-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse delay-4000"></div>
      </div>

      {/* Hero Section */}
      <Section background="white" padding="medium">
        <Container>
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center space-y-4 relative z-10 bg-white/60 backdrop-blur-lg border border-gray-100 shadow-lg rounded-3xl p-6">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl blur-2xl opacity-50"></div>
                  <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                    <Gavel className="w-10 h-10 text-white" />
                  </div>
                </div>
              </motion.div>

              <div className="space-y-4">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-purple-600 font-semibold flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Transparent & Fair Terms
                </motion.p>

                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Terms of <br />
                  <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">Service</span>
                </h1>
              </div>

              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Clear and transparent terms that govern the use of our platform. We believe in honest and fair partnerships with our users.
              </p>

              <div className="flex justify-center gap-4 pt-4">
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <button className="px-8 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 transition-all">
                    Explore Terms ↓
                  </button>
                </motion.div>
              </div>

              <div className="text-xs text-gray-500 pt-2">
                Effective Date: January 16, 2026
              </div>
            </div>
          </FadeIn>
        </Container>
      </Section>

      {/* SLA Metrics Section */}
      <Section background="purple-50" padding="small">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-1 bg-white/60 backdrop-blur-lg border border-purple-100 shadow-lg rounded-2xl p-4">
            {[
              { value: '99.9%', label: 'Uptime SLA', icon: '⚡' },
              { value: '24/7', label: 'Support Available', icon: '📞' },
              { value: '<1 Hr', label: 'Response Time', icon: '⚙️' },
              { value: '100%', label: 'Data Protection', icon: '🔒' }
            ].map((metric, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-100/50 to-pink-100/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative p-5 rounded-2xl bg-white/80 border border-purple-100 hover:border-purple-200 hover:shadow-lg transition-all text-center">
                  <div className="text-5xl mb-2">{metric.icon}</div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-1">{metric.value}</h3>
                  <p className="text-sm text-gray-700">{metric.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Terms Sections */}
      <Section background="blue-50" padding="small">
        <Container>
          <div className="max-w-4xl mx-auto bg-white/60 backdrop-blur-lg border border-blue-100 shadow-lg rounded-3xl p-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-2"
            >
              Complete Terms
            </motion.h2>
            <p className="text-center text-gray-600 mb-6">
              Comprehensive terms that govern your use of our platform
            </p>

            <div className="space-y-2.5">
              {termsData.map((term, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <motion.button
                    onClick={() => setExpandedSection(expandedSection === index ? -1 : index)}
                    className="w-full text-left group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="relative overflow-hidden">
                      <div className={`absolute inset-0 bg-gradient-to-r ${term.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-2xl`}></div>
                      <div className="relative bg-white/80 border border-gray-200 hover:border-purple-300 group-hover:bg-purple-50 transition-all rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${term.color} flex items-center justify-center text-white flex-shrink-0 shadow-lg`}>
                              {term.icon}
                            </div>
                            <div className="text-left">
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                                {term.title}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {term.description}
                              </p>
                            </div>
                          </div>
                          <motion.div
                            animate={{ rotate: expandedSection === index ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronDown className="w-6 h-6 text-purple-500 flex-shrink-0" />
                          </motion.div>
                        </div>

                        <AnimatePresence>
                          {expandedSection === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="mt-6 pt-6 border-t border-white/10"
                            >
                              <ul className="space-y-2">
                                {term.content.map((item, i) => (
                                  <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="flex items-start gap-2"
                                  >
                                    <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-700">{item}</span>
                                  </motion.li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Quick Links Section */}
      <Section background="pink-50" padding="large">
        <Container>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-3 mb-0 mt-0 py-0 bg-white/60 backdrop-blur-lg border border-pink-100 shadow-lg rounded-2xl p-4">
            {[
              { icon: '📋', title: 'Full Terms', desc: 'Read comprehensive terms above', color: 'from-yellow-200 to-yellow-400' },
              { icon: '🔐', title: 'Privacy Policy', desc: 'Learn about data protection', color: 'from-blue-200 to-blue-400' },
              { icon: '⚖️', title: 'Legal Compliance', desc: 'GDPR, CCPA, and more', color: 'from-pink-200 to-pink-400' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -6, scale: 1.03 }}
                className="p-3 rounded-2xl bg-white/60 backdrop-blur-lg border border-gray-100 shadow-lg hover:shadow-xl hover:bg-white/80 transition-all text-center relative"
              >
                <div className={`mx-auto mb-2 w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${item.color} shadow-md`}>
                  <span className="text-5xl">{item.icon}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-1 tracking-tight">{item.title}</h3>
                <p className="text-sm text-gray-700 leading-normal">{item.desc}</p>
                <div className="absolute inset-0 pointer-events-none rounded-2xl border border-white/20" style={{boxShadow: '0 2px 24px 0 rgba(80,80,120,0.08)'}}></div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Contact Section */}
      <Section background="white" padding="large">
        <Container>
          <FadeIn>
            <div className="max-w-2xl mx-auto bg-white/60 backdrop-blur-lg border border-gray-100 shadow-lg rounded-3xl p-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-100/30 to-pink-100/30 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white/80 border border-purple-100 rounded-3xl p-6 space-y-4">
                  <h2 className="text-3xl font-bold text-gray-900 text-center">
                    Questions About Terms?
                  </h2>
                  <p className="text-center text-gray-700">
                    Our legal team is available to clarify any terms or help you understand your rights and obligations.
                  </p>

                  <div className="space-y-3">
                    <div className="flex gap-4 p-3 rounded-xl bg-white/80 hover:bg-purple-50 transition-all border border-purple-100">
                      <FileText className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Legal Support</h3>
                        <p className="text-gray-700">legal@nehainfotech.com</p>
                      </div>
                    </div>
                    <div className="flex gap-4 p-3 rounded-xl bg-white/80 hover:bg-purple-50 transition-all border border-purple-100">
                      <AlertCircle className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Report Issues</h3>
                        <p className="text-gray-700">compliance@nehainfotech.com</p>
                      </div>
                    </div>
                  </div>

                  <ButtonGroup className="pt-2">
                    <Link to="/contact">
                      <ModernButton variant="primary" size="lg" icon={<ArrowRight />}>
                        Contact Legal Team
                      </ModernButton>
                    </Link>
                    <Link to="/">
                      <ModernButton variant="outline" size="lg">
                        Back to Home
                      </ModernButton>
                    </Link>
                  </ButtonGroup>
                </div>
              </div>
            </div>
          </FadeIn>
        </Container>
      </Section>
    </div>
  );
};

export default Terms;
